import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getCustomer, getCustomers, updateCustomer } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import ContactManagement from '../../components/Customer/ContactManagement';
import { StateSelect, CityInput } from '../../components/geo/GeoSelects';
import AddressManagement from '../../components/Customer/AddressManagement';

const EditCustomer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    ediEntityId: '',
    status: 'ACTIVE',
    type: 'WHOLESALE',
    parentId: '',
    classOfTrade: '',
    contractGroup: '',
    tier: '',
    specialty: '',
    website: '',
    city: '',
    state: '',
    territory: '',
    complianceScore: '',
    riskLevel: 'MEDIUM',
    edi844Enabled: false,
    edi845Enabled: false,
    edi849Enabled: false,
    creditLimit: '',
    paymentTerms: '',
    npiNumber: '',
    taxId: '',
    stateLicense: '',
    fdaRegistration: '',
    deaNumber: '',
    notes: ''
  });

  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: customer, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => getCustomer(id),
    enabled: !!id && !!user,
    retry: false
  });

  const { data: customersData } = useQuery({ queryKey: ['customers-for-parent'], queryFn: () => getCustomers({ limit: 1000 }) });
  const parentOptions = (Array.isArray(customersData) ? customersData : (customersData?.customers || customersData?.data || [])).filter((c) => c.id !== id);

  const updateMutation = useMutation({
    mutationFn: (data) => updateCustomer(id, data),
    onSuccess: () => {
      toast.success('Customer updated successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      navigate(`/app/customers/${id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error updating customer');
      setIsSubmitting(false);
    }
  });

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        ediEntityId: customer.ediEntityId || '',
        status: customer.isActive === false ? 'INACTIVE' : 'ACTIVE',
        type: customer.type || 'WHOLESALE',
        parentId: customer.parentId || '',
        classOfTrade: customer.classOfTrade || '',
        contractGroup: customer.contractGroup || '',
        tier: customer.tier || '',
        specialty: customer.specialty || '',
        website: customer.website || '',
        territory: customer.territory || '',
        complianceScore: customer.complianceScore || '',
        riskLevel: customer.riskLevel || 'MEDIUM',
        edi844Enabled: customer.edi844Enabled || false,
        edi845Enabled: customer.edi845Enabled || false,
        edi849Enabled: customer.edi849Enabled || false,
        city: customer.city || '',
        state: customer.state || '',
        creditLimit: customer.creditLimit || '',
        paymentTerms: customer.paymentTerms || '',
        npiNumber: customer.npiNumber || '',
        taxId: customer.taxId || '',
        stateLicense: customer.stateLicense || '',
        fdaRegistration: customer.fdaRegistration || '',
        deaNumber: customer.deaNumber || '',
        notes: customer.notes || ''
      });

      // Normalize the API shapes into what the child managers expect.
      // Contacts come back with a single `role`; ContactManagement reads a
      // `contactRoles` array. Addresses nest street/city/state under `usAddress`;
      // AddressManagement reads them flat. Without this remap the lists render
      // blank after a save+refetch and edits read `undefined`.
      setContacts((customer.contacts || []).map(c => ({
        ...c,
        contactRoles: Array.isArray(c.contactRoles) && c.contactRoles.length
          ? c.contactRoles
          : (c.role ? [{ id: `role-${c.id}`, role: c.role, isDefault: !!c.isPrimary, isActive: true }] : [])
      })));
      setAddresses((customer.addresses || []).map(a => ({
        ...a,
        street: a.street ?? a.usAddress?.street1 ?? '',
        street2: a.street2 ?? a.usAddress?.street2 ?? '',
        city: a.city ?? a.usAddress?.city ?? '',
        state: a.state ?? a.usAddress?.state ?? '',
        zipCode: a.zipCode ?? a.usAddress?.zipCode ?? '',
        county: a.county ?? a.usAddress?.county ?? '',
        country: a.country ?? a.usAddress?.country ?? 'USA'
      })));
    }
  }, [customer]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        // Include contacts and addresses in the update
        contacts: contacts.map(contact => ({
          id: contact.id.startsWith('temp-') ? undefined : contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          title: contact.title,
          department: contact.department,
          email: contact.email,
          phone: contact.phone,
          mobile: contact.mobile,
          fax: contact.fax,
          isPrimary: contact.isPrimary,
          isActive: contact.isActive,
          notes: contact.notes,
          contactRoles: contact.contactRoles?.map(role => ({
            id: role.id.startsWith('temp-role-') ? undefined : role.id,
            role: role.role,
            isDefault: role.isDefault,
            isActive: role.isActive,
            notes: role.notes
          }))
        })),
        addresses: addresses.map(address => ({
          id: address.id.startsWith('temp-') ? undefined : address.id,
          type: address.type,
          name: address.name,
          company: address.company,
          street: address.street,
          street2: address.street2,
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
          country: address.country,
          county: address.county,
          phone: address.phone,
          email: address.email,
          attention: address.attention,
          isDefault: address.isDefault,
          isActive: address.isActive,
          notes: address.notes
        }))
      };

      await updateMutation.mutateAsync(submitData);
    } catch (error) {
      console.error('Submit error:', error);
      setIsSubmitting(false);
    }
  };

  const handleContactsChange = (newContacts) => {
    setContacts(newContacts);
  };

  const handleAddressesChange = (newAddresses) => {
    setAddresses(newAddresses);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading customer...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">Error loading customer</p>
          <button
            onClick={() => navigate('/app/customers')}
            className="mt-4 text-primary-600 hover:text-primary-700"
            title="Back to Customers"
          >
            Back to Customers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/app/customers')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            title="Back to Customers"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Customers
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Edit Customer</h1>
          <p className="text-gray-600 mt-2">Update customer information, contacts, and addresses</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">EDI Entity ID *</label>
                <input
                  type="text"
                  name="ediEntityId"
                  value={formData.ediEntityId}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.ediEntityId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.ediEntityId && <p className="text-red-500 text-sm mt-1">{errors.ediEntityId}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="WHOLESALE">Wholesale</option>
                  <option value="DIRECT">Direct</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Parent Customer</label>
                <select
                  name="parentId"
                  value={formData.parentId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">None (top-level account)</option>
                  {parentOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Link under a parent (IDN, chain HQ, GPO parent); level is derived from the hierarchy.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Class of Trade</label>
                <select
                  name="classOfTrade"
                  value={formData.classOfTrade}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select Class of Trade</option>
                  <option value="RETAIL_PHARMACY">Retail Pharmacy</option>
                  <option value="HOSPITAL">Hospital</option>
                  <option value="LONG_TERM_CARE">Long-term Care Facility</option>
                  <option value="SPECIALTY_PHARMACY">Specialty Pharmacy</option>
                  <option value="WHOLESALER">Wholesaler/Distributor</option>
                  <option value="GOVERNMENT_FACILITY">Government Facility</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contract Group</label>
                <input
                  type="text"
                  name="contractGroup"
                  value={formData.contractGroup}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tier</label>
                <input
                  type="text"
                  name="tier"
                  value={formData.tier}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Specialty</label>
                <input
                  type="text"
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Territory</label>
                <input
                  type="text"
                  name="territory"
                  value={formData.territory}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* EDI and Technical Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">EDI & Technical Settings</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Compliance Score</label>
                <input
                  type="number"
                  name="complianceScore"
                  value={formData.complianceScore}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Risk Level</label>
                <select
                  name="riskLevel"
                  value={formData.riskLevel}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="edi844Enabled"
                  checked={formData.edi844Enabled}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">EDI 844 Enabled (Sales/Chargeback)</label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="edi845Enabled"
                  checked={formData.edi845Enabled}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">EDI 845 Enabled (Price Notification)</label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="edi849Enabled"
                  checked={formData.edi849Enabled}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">EDI 849 Enabled (Response to Chargeback)</label>
              </div>

            </div>
          </div>

          {/* Financial Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Financial Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Credit Limit</label>
                <input
                  type="number"
                  name="creditLimit"
                  value={formData.creditLimit}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
                <input
                  type="text"
                  name="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={handleInputChange}
                  placeholder="e.g., NET30"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Compliance and Identifiers Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Compliance & Identifiers</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">NPI Number</label>
                <input
                  type="text"
                  name="npiNumber"
                  value={formData.npiNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax ID</label>
                <input
                  type="text"
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <CityInput
                  name="city"
                  value={formData.city}
                  state={formData.state}
                  onChange={handleInputChange}
                  placeholder="e.g. Albany"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <StateSelect
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State License</label>
                <input
                  type="text"
                  name="stateLicense"
                  value={formData.stateLicense}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">FDA Registration</label>
                <input
                  type="text"
                  name="fdaRegistration"
                  value={formData.fdaRegistration}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">DEA Number</label>
                <input
                  type="text"
                  name="deaNumber"
                  value={formData.deaNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Additional Information</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Additional notes about the customer..."
              />
            </div>
          </div>

          {/* Contact Management Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <ContactManagement
              customerId={id}
              contacts={contacts}
              onContactsChange={handleContactsChange}
            />
          </div>

          {/* Address Management Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <AddressManagement
              customerId={id}
              addresses={addresses}
              onAddressesChange={handleAddressesChange}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title="Update Customer"
            >
              <Save size={20} />
              {isSubmitting ? 'Updating...' : 'Update Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCustomer;
